import React from 'react';
import { Link } from 'react-router-dom';

/**
 * - `primary`: filled red, the main action of a section (black text: white on red is too faint)
 * - `outline`: red border, secondary actions next to content ("Összes cikk", "Több betöltése")
 * - `secondary`: grey border, the second choice next to a primary button
 * - `ghost`: text only, for "close" / "later" type choices
 * - `spotlight`: black with a rotating red edge, for the one highlighted call to action
 */
export type ButtonVariant = 'primary' | 'outline' | 'secondary' | 'ghost' | 'spotlight';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

const BASE =
  'relative inline-flex items-center justify-center gap-2 font-mono font-bold uppercase tracking-widest text-center transition-colors duration-200 ease-smooth cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-neon-red focus-visible:ring-offset-2 focus-visible:ring-offset-black disabled:cursor-wait disabled:opacity-60 disabled:pointer-events-none';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-neon-red text-black border border-neon-red hover:bg-white hover:border-white',
  outline: 'border border-neon-red text-neon-red hover:bg-neon-red hover:text-black',
  secondary: 'border border-neutral-600 text-white hover:border-neon-red hover:text-neon-red',
  ghost: 'text-neutral-400 hover:text-white',
  // The padding shows 2px of the spinning gradient around the black inner span
  spotlight: 'group overflow-hidden p-[2px] text-white hover:text-neon-red',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-sm',
  lg: 'px-8 py-4 text-base',
  icon: 'px-4 py-3',
};

interface StyleProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
}

/** The button classes, for the rare element that can't be one of the components below. */
export function buttonClasses({ variant = 'primary', size = 'md', className = '' }: StyleProps) {
  // Spotlight puts the padding on its inner span, so the gradient edge stays 2px wide
  const sizeClasses = variant === 'spotlight' ? SIZES[size].replace(/p[xy]-\S+/g, '') : SIZES[size];
  return `${BASE} ${VARIANTS[variant]} ${sizeClasses} ${className}`.replace(/\s+/g, ' ').trim();
}

const Content: React.FC<{
  variant: ButtonVariant;
  size: ButtonSize;
  children: React.ReactNode;
}> = ({ variant, size, children }) => {
  if (variant !== 'spotlight') return <>{children}</>;
  const padding = SIZES[size].match(/p[xy]-\S+/g)?.join(' ') ?? '';
  return (
    <>
      <span
        className="absolute inset-[-1000%] bg-spotlight motion-safe:animate-spin-slow"
        aria-hidden="true"
      />
      <span
        className={`relative z-10 inline-flex h-full w-full items-center justify-center gap-2 bg-black transition-colors duration-200 group-hover:bg-neutral-900 ${padding}`}
      >
        {children}
      </span>
    </>
  );
};

type ButtonProps = StyleProps & React.ButtonHTMLAttributes<HTMLButtonElement>;

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  className,
  type = 'button',
  children,
  ...rest
}) => (
  <button type={type} className={buttonClasses({ variant, size, className })} {...rest}>
    <Content variant={variant} size={size}>
      {children}
    </Content>
  </button>
);

type ButtonLinkProps = StyleProps & {
  children: React.ReactNode;
} & (
    | ({ to: string; href?: never } & Omit<React.ComponentProps<typeof Link>, 'to' | 'className'>)
    | ({ href: string; to?: never } & React.AnchorHTMLAttributes<HTMLAnchorElement>)
  );

/** A link that looks like a button: `to` for pages of the site, `href` for external sites (new tab). */
export const ButtonLink: React.FC<ButtonLinkProps> = ({
  variant = 'primary',
  size = 'md',
  className,
  children,
  ...rest
}) => {
  const classes = buttonClasses({ variant, size, className });
  const content = (
    <Content variant={variant} size={size}>
      {children}
    </Content>
  );

  if ('to' in rest && rest.to !== undefined) {
    return (
      <Link className={classes} {...rest}>
        {content}
      </Link>
    );
  }
  return (
    <a
      className={classes}
      target="_blank"
      rel="noopener noreferrer"
      {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
    >
      {content}
    </a>
  );
};
